import { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { SectionCollapseButton, useSectionCollapse } from '../../../shared/components/SectionCollapseButton';
import { SEMANTIC_COLOR_VARS, hashSemanticColor } from '../../../shared/components/semanticColor';
import { formatEntityTypeLabel } from '../../../shared/components/entityTypeLabel';
import type { Entity, ProjectMonitoringEntityTab, ProjectMonitoringEntityType } from '../../../core/types/data-models';
import { AddTabDialog, TabIconGlyph } from '../../projects/dialogs/AddTabDialog';

// ── Main Entities & Knowledge Context (project dashboard block) ──────────────
//
// Which entity types show here (and in what order) is configured per project
// via ProjectMonitoringEntityType rows, editable inline from this block.
// entityType is an open-ended LLM-assigned label (generate-document-metadata-
// entities.prompt.md), not a fixed enum, so the "Add entity type" options come from
// whatever types have actually been extracted for this project rather than a
// hardcoded list — that stays correct as new types get invented.

// Rows past this many distinct entities collapse behind a "+N more" toggle,
// with managed entities pinned first so they're never the ones hidden.
const ENTITY_DISPLAY_LIMIT = 19;

export function MainEntitiesSection({
  entityTypeRows,
  entityTabs,
  entities,
  entitiesLoading,
  allEntityTypes,
  canEdit,
  activeTabId,
  onTabChange,
  onAddType,
  onDeleteType,
  onAddTab,
  onDeleteTab,
  onOpenEntity,
}: {
  /** Every configured row across every tab — filtered to the active tab below. */
  entityTypeRows: ProjectMonitoringEntityType[];
  /** User-defined tabs grouping the rows above. The "General" tab (tabId: null) isn't one of these — it's implicit. */
  entityTabs: ProjectMonitoringEntityTab[];
  /** Entities for the active tab's configured types only — fetched per tab, not for the whole project up front. */
  entities: Entity[];
  /** True while `entities` is being (re)fetched for the current tab, e.g. right after switching tabs. */
  entitiesLoading: boolean;
  /** Every entityType extracted for the project so far — the "Add" dropdown's pool. */
  allEntityTypes: string[];
  canEdit: boolean;
  /** Which tab is showing (null = the implicit "General" tab). Owned by the parent so it can scope the entities fetch. */
  activeTabId: number | null;
  onTabChange: (tabId: number | null) => void;
  onAddType: (entityType: string, tabId: number | null) => void;
  onDeleteType: (id: number) => void;
  onAddTab: (name: string, icon: string | null) => Promise<unknown>;
  onDeleteTab: (id: number) => void;
  onOpenEntity: (entity: Entity) => void;
}) {
  const [addTabOpen, setAddTabOpen] = useState(false);
  const [expanded, toggleExpanded] = useSectionCollapse('mainEntities');
  // Rows (by ProjectMonitoringEntityType id) currently showing their full entity list.
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggleRowExpanded = (rowId: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  if (entityTypeRows.length === 0 && entityTabs.length === 0 && !canEdit) return null;

  const byType = new Map<string, Entity[]>();
  for (const e of entities) {
    const list = byType.get(e.entityType) ?? [];
    list.push(e);
    byType.set(e.entityType, list);
  }

  // Same entityType can be configured in more than one tab (separate rows), so
  // "already used" / "available to add" is scoped to the active tab only.
  const rowsInActiveTab = entityTypeRows.filter((r) => r.tabId === activeTabId);
  const usedTypesInTab = new Set(rowsInActiveTab.map((r) => r.entityType));
  const availableTypes = allEntityTypes.filter((t) => !usedTypesInTab.has(t));

  // A lone, un-deletable "General" tab isn't worth a visible tab strip unless
  // the user could add more.
  const showTabStrip = entityTabs.length > 0 || canEdit;

  return (
    <GlassPanel sx={{ p: 2.5 }}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ mb: expanded ? (showTabStrip ? 1.5 : 2) : 0 }}
      >
        <IconTile icon={<AccountTreeIcon fontSize="inherit" />} color="cyan" size="sm" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            Main Entities &amp; Knowledge Context
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Click any entity tag to view details
          </Typography>
        </Box>
        <SectionCollapseButton expanded={expanded} onToggle={toggleExpanded} label="Main Entities" />
      </Stack>

      <Collapse in={expanded}>
        {showTabStrip && (
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1.5, rowGap: 1 }}>
            <Chip
              label="General"
              size="small"
              color={activeTabId === null ? 'primary' : 'default'}
              variant={activeTabId === null ? 'filled' : 'outlined'}
              onClick={() => onTabChange(null)}
            />
            {entityTabs.map((tab) => (
              <Chip
                key={tab.id}
                icon={tab.icon ? <TabIconGlyph icon={tab.icon} /> : undefined}
                label={tab.name}
                size="small"
                color={activeTabId === tab.id ? 'primary' : 'default'}
                variant={activeTabId === tab.id ? 'filled' : 'outlined'}
                onClick={() => onTabChange(tab.id)}
                onDelete={canEdit ? () => onDeleteTab(tab.id) : undefined}
              />
            ))}
            {canEdit && (
              <Chip
                icon={<AddIcon fontSize="small" />}
                label="Add tab"
                size="small"
                variant="outlined"
                onClick={() => setAddTabOpen(true)}
              />
            )}
          </Stack>
        )}

        <Stack spacing={1.5}>
          {rowsInActiveTab.map((row) => {
            const list = byType.get(row.entityType) ?? [];
            // Managed entities are pinned first so a collapsed row never hides one.
            const sorted = [...list].sort((a, b) => Number(b.isManaged) - Number(a.isManaged));
            const isExpanded = expandedRows.has(row.id);
            const hasMore = sorted.length > ENTITY_DISPLAY_LIMIT;
            const visible = isExpanded ? sorted : sorted.slice(0, ENTITY_DISPLAY_LIMIT);
            const rowColor = hashSemanticColor(row.entityType);
            return (
              <Stack
                key={row.id}
                direction="row"
                spacing={2}
                alignItems="flex-start"
                sx={{
                  pt: 1.25,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  '&:hover .entity-row-delete-btn, &:focus-within .entity-row-delete-btn': { opacity: 1 },
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ width: 170, flexShrink: 0 }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: SEMANTIC_COLOR_VARS[rowColor].main,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.03em', color: SEMANTIC_COLOR_VARS[rowColor].main }}
                  >
                    {formatEntityTypeLabel(row.entityType).toUpperCase()}
                  </Typography>
                </Stack>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                  {list.length === 0 ? (
                    entitiesLoading ? (
                      <Skeleton variant="rounded" width={90} height={22} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        None identified yet
                      </Typography>
                    )
                  ) : (
                    <>
                      {visible.map((e) => (
                        <Tooltip key={e.id} title={e.entityDescription ?? ''} disableHoverListener={!e.entityDescription}>
                          <Chip
                            label={e.entityName}
                            size="small"
                            variant="outlined"
                            onClick={() => onOpenEntity(e)}
                            sx={[
                              {
                                bgcolor: 'background.paper',
                                borderColor: 'divider',
                                boxShadow: (theme) => (theme.palette.mode === 'dark' ? 'none' : 'var(--elevation-sm)'),
                                transition: 'border-color var(--transition)',
                                '&:hover': { borderColor: 'var(--color-primary-line)' },
                              },
                              e.isManaged && {
                                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.15),
                                border: '1.5px solid',
                                borderColor: 'warning.main',
                                fontWeight: 600,
                                '&:hover': { borderColor: 'warning.main' },
                              },
                            ]}
                          />
                        </Tooltip>
                      ))}
                      {hasMore && (
                        <Chip
                          label={isExpanded ? 'Show less' : `+${sorted.length - ENTITY_DISPLAY_LIMIT} more`}
                          size="small"
                          variant="outlined"
                          onClick={() => toggleRowExpanded(row.id)}
                          sx={{
                            bgcolor: 'background.paper',
                            fontStyle: 'italic',
                            borderStyle: 'dashed',
                          }}
                        />
                      )}
                    </>
                  )}
                </Box>
                {canEdit && (
                  <Tooltip title="Remove from this view">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDeleteType(row.id)}
                      className="entity-row-delete-btn"
                      sx={{ mt: -0.5, opacity: 0, transition: 'opacity 0.15s' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            );
          })}

          {canEdit && availableTypes.length > 0 && (
            <FormControl size="small" sx={{ width: 220 }}>
              <Select
                displayEmpty
                value=""
                onChange={(e) => {
                  if (e.target.value) onAddType(e.target.value, activeTabId);
                }}
                renderValue={() => (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
                    <AddIcon fontSize="small" />
                    <Typography variant="body2">Add entity type</Typography>
                  </Stack>
                )}
              >
                {availableTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {formatEntityTypeLabel(t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Collapse>

      <AddTabDialog open={addTabOpen} onClose={() => setAddTabOpen(false)} onSubmit={onAddTab} />
    </GlassPanel>
  );
}
